import React, { useState } from 'react';
import { RSInput, RSTextarea, RSCheckbox, RSButton } from 'reactsymbols-kit';
import MerlinSelect from '../MerlinSelect';
import Styles from '../../styles/colors';
import AddObjectController from '../../merode/controllers/AddObjectController';
import { isEmpty } from 'lodash';
import { useController, useAsynchronousAction } from '../../hooks';
import type { IModelVersion as IControllerModelVersion } from '../../merode/controllers/Controller';
import type { IModelVersion } from '../../stores/projects/model_version';
import type { Position, ObjectProperties, AutoEventsAndMethods } from '../../merode/commands/types';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSInputChange, type RSTextareaChange, type RSCheckboxChange } from '../Inspector/rsForm';

/** The mutable form state (was the class's `this.state`). */
interface Fields {
  name: string;
  description: string;
  abstract: boolean;
  auto: boolean;
  create_method_name: string | null;
  create_event_name: string | null;
  end_method_name: string | null;
  end_event_name: string | null;
}

/** Keys of the four generated element-name fields (subset of `Fields`). */
type ElementNameKey = 'create_event_name' | 'end_event_name' | 'create_method_name' | 'end_method_name';
/** Keys of the two event-name dropdown fields. */
type EventNameKey = 'create_event_name' | 'end_event_name';

/** A label/value option in the Creatable event dropdown. */
interface EventOption {
  label: string;
  value: string;
}

interface AddObjectFormProps {
  /** The ModelVersion to add the object to. */
  modelVersion: IModelVersion;
  /** The position for the new object. */
  position?: Position | null;
  /** Callback to cancel adding a new object. */
  onCancel?: () => void;
  /** Callback to send the report up. */
  onReport?: (report: Report) => void;
}

/**
 * Form to add an object type.
 *
 * Props:
 * - modelVersion: the ModelVersion to add the object to.
 * - position: the position for the new object.
 * - onCancel: callback to cancel adding a new object.
 * - onReport: callback to send the report up.
 */
function AddObjectModal({ modelVersion, position, onCancel, onReport }: AddObjectFormProps) {
  // Store `IModelVersion` is the same loaded node the controller's narrower
  // `IModelVersion` describes; cast at the construction boundary only.
  const controller = useController(
    () => new AddObjectController(modelVersion as unknown as IControllerModelVersion)
  );

  // The dynamic `setState({ [key]: value })` updates (name, description, the
  // four element-name fields) are kept in a single object that we spread-merge
  // into, preserving the class's setState merge semantics. Initialised from the
  // constructor.
  const [fields, setFields] = useState<Fields>(() => ({
    name: '',
    description: '',
    abstract: false,
    auto: modelVersion.model.generateMethods,
    create_method_name: null,
    create_event_name: null,
    end_method_name: null,
    end_event_name: null,
  }));

  // Merge helper reproducing this.setState(partial).
  const updateFields = (change: Partial<Fields>) => {
    setFields((current) => ({ ...current, ...change }));
  };

  // The async add flow. busy reproduces `working`; onReport fires on success.
  // The hook forwards args/result untyped; the action's typed signature is cast
  // to the loose shape, and the resolved `Report` is cast in the callback.
  const addAction = useAsynchronousAction(
    ((properties: ObjectProperties, position: Position | undefined, options: AutoEventsAndMethods) =>
      controller.addObject(properties, position, options)) as (...args: unknown[]) => Promise<unknown>,
    (report) => {
      if (onReport) {
        onReport(report as Report);
      }
    }
  );
  const working = addAction.busy;

  // Get the element name either from state or from the given defaults
  const elementName = (key: ElementNameKey, defaults: Record<ElementNameKey, string>): string => {
    return isEmpty(fields[key]) ? defaults[key] : (fields[key] as string);
  };

  // Get all element names (was the `elementNames` getter)
  const getElementNames = () => {
    if (!fields.auto) {
      return {
        create_event_name: null,
        end_event_name: null,
        create_method_name: null,
        end_method_name: null,
      };
    } else {
      const defaults = controller.defaults(fields.name);
      return {
        create_event_name: elementName('create_event_name', defaults),
        end_event_name: elementName('end_event_name', defaults),
        create_method_name: elementName('create_method_name', defaults),
        end_method_name: elementName('end_method_name', defaults),
      };
    }
  };

  /**
   * Actually add the object type.
   */
  const onAdd = () => {
    const { name, description, abstract, auto } = fields;
    const {
      create_event_name,
      end_event_name,
      create_method_name,
      end_method_name,
    } = getElementNames();
    addAction.execute(
      {
        name,
        description,
        abstract,
      },
      position,
      {
        auto,
        create_event_name,
        end_event_name,
        create_method_name,
        end_method_name,
      }
    );
  };

  // Setter for an event name (create_event_name, end_event_name): clear the
  // value in our state if it is equal to the default.
  const setEventName = (key: EventNameKey, value: string | null) => {
    if (value === controller.defaults(fields.name)[key]) {
      updateFields({ [key]: null });
    } else {
      updateFields({ [key]: value });
    }
  };

  // Validation (was the `valid` getter)
  const isValid = () => {
    let keys: (keyof Fields)[] = ['name'];
    if (fields.auto) {
      keys = keys.concat([
        'create_method_name',
        'create_event_name',
        'end_method_name',
        'end_event_name',
      ]);
    }
    // Check all names. `fields[key]` is `string | null` for these keys; the
    // controller handles `null` via `isEmpty`, matching the original JS.
    const checks = keys.map((key) => controller.checkNameInput(fields[key] as string));
    return checks.every((c) => c.ok) && !isEmpty(fields.name);
  };
  const valid = isValid();

  // Get the options for an event dropdown (with given key, i.e.
  // create_event_name or end_event_name)
  const eventOptions = (key: EventNameKey): EventOption[] => {
    const name = controller.defaults(fields.name)[key];
    const result: EventOption[] = [
      {
        label: `${name} (New)`,
        value: name,
      },
    ];
    const existing = controller.existingEventNames;
    if (!isEmpty(fields[key]) && !existing.includes(fields[key] as string)) {
      result.push({
        label: `${fields[key]} (New)`,
        value: fields[key] as string,
      });
    }
    result.push(...existing.map((en) => ({ label: en, value: en })));
    return result;
  };

  // render a name input, including error message when invalid
  const renderNameInput = (key: keyof Fields, label: string, value: string, disabled?: boolean) => {
    const check = controller.checkNameInput(value);
    return (
      <RSInput
        autoFocus={key === 'name'}
        name={key}
        label={label}
        value={value}
        onChange={asRSChange<RSInputChange>((value: string) => updateFields({ [key]: value }))}
        disabled={disabled !== undefined ? disabled : working}
        level={rsValue(check.ok ? null : 'danger')}
        text={rsValue(check.ok ? null : check.message)}
      />
    );
  };

  // render a dropdown for the event name with the given key
  const renderEventNameSelect = (key: EventNameKey, label: string, value: string | null) => {
    const check = controller.checkNameInput(value as string);
    return (
      <div
        className={`rs-element rs-input-container rs-input event-name-${key} ${
          check.ok ? '' : 'rs-input--danger'
        }`}
      >
        <label className="rs-element rs-form-label">{label}</label>
        <MerlinSelect
          creatable
          promptTextCreator={(label: string) => `${label} (New)`}
          name={key}
          options={eventOptions(key)}
          value={rsValue(value)}
          clearable={false}
          placeholder=""
          // MerlinSelect forwards the selected option (or `null` on clear),
          // preserving the v1 contract this form relies on (it clears the value
          // by passing `null`).
          onChange={(change) =>
            setEventName(key, change != null ? (change.value as string) : null)
          }
          disabled={working || !fields.auto}
        />
        {check.ok ? null : (
          <span className="rs-input__text">
            <p>{check.message}</p>
          </span>
        )}
      </div>
    );
  };

  const names = getElementNames();
  return (
    <div className="merlin-modal add-object-form">
      <div className="title">Add object</div>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !working) {
            onAdd();
          }
        }}
      >
        <div className="modal-body">
          <div className="container">
            <div className="row">
              <div className="col">{renderNameInput('name', 'Object name', fields.name)}</div>
            </div>
            <div className="row">
              <div className="col">
                <RSTextarea
                  name="description"
                  label="Description"
                  value={fields.description}
                  onChange={asRSChange<RSTextareaChange>((value: string) => updateFields({ description: value }))}
                  disabled={working}
                />
              </div>
            </div>
            {/* <RSCheckbox
            label='Object is abstract'
            checked={this.state.abstract}
            onChange={(value, checked) => this.setState({abstract: checked})}
            disabled={this.state.working}
          /> */}
            <div className="separator" />
            <div className="row">
              <div className="col">
                <RSCheckbox
                  label="Automatically add a create and end method"
                  checked={fields.auto}
                  onChange={asRSChange<RSCheckboxChange>(
                    (value: string | number, checked: boolean) => updateFields({ auto: checked })
                  )}
                  disabled={working}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-2">
                {renderNameInput(
                  'create_method_name',
                  'Creation method name',
                  names.create_method_name || '',
                  working || !fields.auto
                )}
              </div>
              <div className="col-2">
                {renderEventNameSelect(
                  'create_event_name',
                  'Creation event',
                  names.create_event_name
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-2">
                {renderNameInput(
                  'end_method_name',
                  'Ending method name',
                  names.end_method_name || '',
                  working || !fields.auto
                )}
              </div>
              <div className="col-2">
                {renderEventNameSelect('end_event_name', 'Ending event', names.end_event_name)}
              </div>
            </div>
          </div>
        </div>

        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onCancel}
          />
          <RSButton
            value={rsValue(working ? null : 'Add')}
            type="submit"
            background={Styles.blueGradient}
            disabled={!valid}
            iconName={rsValue(working ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(working ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
}

export default AddObjectModal;
